import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Descriptions, Space, Table, Tag } from "antd";
import { DocLink, type DocLinkType } from "../../components/DocLink";
import { PageHeader } from "../../components/PageHeader";
import { SiteLabel } from "../../components/SiteLabel";
import { useApi } from "../../helpers/useApi";
import type { MRN, DocRef } from "../../api-models";

const SOURCE_LABELS: Record<string, string> = {
  GIN: "Goods Issue (GIN)",
  TRANSFER_NOTE: "Transfer Note",
  PO: "Direct from supplier (PO)",
  GIN_DIRECT: "Supplier to site (GIN Direct)",
};

// tygo models Go's anonymous struct embedding (MRNDetail) as a nested field, but
// encoding/json actually flattens it — this local type matches the real wire shape.
type MRNDetail = MRN & { sourceDoc?: DocRef; requests: DocRef[] };

// The server resolves MRN's polymorphic source into a DocType the route table knows
// (helpers/docRoutes.ts), so both the dispatch document and the requests behind it link
// through the same component.
function refLink(ref: DocRef) {
  return <DocLink docType={ref.docType as DocLinkType} id={ref.id} docNo={ref.docNo} />;
}

// A posted MRN is immutable: it never changes the dispatch document's quantities
// (spec §31.4), and correcting one means posting a reversal, not editing this page.
export default function MRNDetailPage() {
  const { id } = useParams();
  const { request } = useApi();
  const [mrn, setMrn] = useState<MRNDetail | null>(null);

  useEffect(() => {
    request<MRNDetail>({ url: `/mrns/${id}` }).then(setMrn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!mrn) return null;
  const requests = mrn.requests ?? [];

  return (
    <div>
      <PageHeader
        title={mrn.docNo}
        subtitle={`Received ${new Date(mrn.receivedAt).toLocaleString()}`}
        extra={<Tag>{SOURCE_LABELS[mrn.sourceType] ?? mrn.sourceType}</Tag>}
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Source document">
            {mrn.sourceDoc ? refLink(mrn.sourceDoc) : `#${mrn.sourceId}`}
          </Descriptions.Item>
          <Descriptions.Item label="Destination"><SiteLabel site={mrn.site} fallback="Central Warehouse" /></Descriptions.Item>
          <Descriptions.Item label={requests.length > 1 ? "Requests" : "Request"}>
            {requests.length === 0
              ? "—"
              : requests.map((req, i) => (
                  <Fragment key={req.id}>
                    {i > 0 && ", "}
                    {refLink(req)}
                  </Fragment>
                ))}
          </Descriptions.Item>
          <Descriptions.Item label="Supplier GDN">{mrn.supplierGdnNo || "—"}</Descriptions.Item>
          <Descriptions.Item label="Notes">{mrn.notes || "—"}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Received quantities">
        <Table
          rowKey="id"
          dataSource={mrn.lines ?? []}
          pagination={false}
          columns={[
            { title: "Code", render: (_, r) => r.item?.code },
            { title: "Item", render: (_, r) => r.item?.name },
            { title: "Accepted", dataIndex: "acceptedQty" },
            { title: "Damaged", dataIndex: "damagedQty", render: (v: number) => (v > 0 ? <Tag color="orange">{v}</Tag> : v) },
            { title: "Rejected", dataIndex: "rejectedQty", render: (v: number) => (v > 0 ? <Tag color="red">{v}</Tag> : v) },
            { title: "Remarks", dataIndex: "remarks", render: (v) => v || "—" },
          ]}
        />
        <Space style={{ marginTop: 12 }} direction="vertical" size={2}>
          <small>Accepted lands at the destination. Damaged goes to that location&apos;s quarantine, outside Available stock.</small>
          <small>Rejected heads back toward the source the goods came from — the warehouse or site that dispatched them, or the supplier on a GIN Direct; a shortfall against the dispatch opens a discrepancy.</small>
        </Space>
      </Card>
    </div>
  );
}
